import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { communicationService, Message, Notification } from '../services/communicationService';
import { useNotificationHelpers } from './NotificationSystem';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';

interface AdminMessagingProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminMessaging: React.FC<AdminMessagingProps> = ({ isOpen, onClose }) => {
  const { userProfile } = useAuth();
  const { showSuccess, showError } = useNotificationHelpers();
  const [activeTab, setActiveTab] = useState<'messages' | 'compose'>('messages');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  
  // Compose message state
  const [composeData, setComposeData] = useState({
    toEmail: '',
    toName: '',
    subject: '',
    message: '',
    type: 'general' as const
  });

  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen]);

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const messagesData = await communicationService.getAllMessagesForAdmin();
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading messages:', error);
      showError('Loading Error', 'Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageClick = async (message: Message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);

    // Mark as read if not already read
    if (!message.isRead && message.id) {
      await communicationService.markMessageAsRead(message.id);
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, isRead: true } : m
      ));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userProfile?.email) {
      showError('Error', 'User profile not found');
      return;
    }

    try {
      await communicationService.sendMessage({
        fromUserId: userProfile.uid,
        fromUserEmail: userProfile.email,
        fromUserName: userProfile.displayName || 'Admin',
        toUserEmail: composeData.toEmail,
        toUserName: composeData.toName,
        subject: composeData.subject,
        message: composeData.message,
        type: composeData.type,
        isRead: false,
        isAdminMessage: true
      });

      showSuccess('Message Sent', 'Message has been sent successfully');
      setComposeData({
        toEmail: '',
        toName: '',
        subject: '',
        message: '',
        type: 'general'
      });
      setActiveTab('messages');
      loadMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Send Error', 'Failed to send message');
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'quote_inquiry':
        return '📋';
      case 'quote_response':
        return '💬';
      case 'invoice_notification':
        return '💰';
      case 'payment_notification':
        return '💳';
      case 'general':
        return '📝';
      default:
        return '📧';
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Admin Messaging">
      <div className="w-full max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'messages'
                ? 'border-b-2 border-avis-blue text-avis-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Messages ({messages.length})
          </button>
          <button
            onClick={() => setActiveTab('compose')}
            className={`px-6 py-3 font-medium text-sm ${
              activeTab === 'compose'
                ? 'border-b-2 border-avis-blue text-avis-blue'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Compose Message
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" message="Loading..." />
          </div>
        ) : (
          <>
            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-4">📭</div>
                    <p>No messages yet</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      onClick={() => handleMessageClick(message)}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        message.isRead 
                          ? 'bg-white border-gray-200 hover:bg-gray-50' 
                          : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xl">{getMessageTypeIcon(message.type)}</span>
                            <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                            {!message.isRead && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            <p>
                              {message.isAdminMessage ? 'To' : 'From'}: {message.isAdminMessage ? message.toUserName : message.fromUserName}
                            </p>
                            <p>Email: {message.isAdminMessage ? message.toUserEmail : message.fromUserEmail}</p>
                          </div>
                          <p className="text-gray-700 mt-2 line-clamp-2">
                            {message.message}
                          </p>
                        </div>
                        <div className="text-right text-sm text-gray-500 ml-4">
                          {formatDate(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Compose Tab */}
            {activeTab === 'compose' && (
              <form onSubmit={handleSendMessage} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={composeData.toEmail}
                      onChange={(e) => setComposeData(prev => ({ ...prev, toEmail: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                      placeholder="client@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={composeData.toName}
                      onChange={(e) => setComposeData(prev => ({ ...prev, toName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                      placeholder="Client Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    required
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                    placeholder="Message subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Type
                  </label>
                  <select
                    value={composeData.type}
                    onChange={(e) => setComposeData(prev => ({ ...prev, type: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                  >
                    <option value="general">General</option>
                    <option value="quote_response">Quote Response</option>
                    <option value="invoice_notification">Invoice Notification</option>
                    <option value="payment_notification">Payment Notification</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={composeData.message}
                    onChange={(e) => setComposeData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avis-blue focus:border-avis-blue"
                    placeholder="Type your message here..."
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('messages')}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-avis-blue text-white rounded-lg hover:bg-avis-dark transition-colors"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>

      {/* Message Detail Modal */}
      {showMessageModal && selectedMessage && (
        <Modal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          title={selectedMessage.subject}
        >
          <div className="space-y-4">
            <div className="border-b pb-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600">
                    {selectedMessage.isAdminMessage ? 'To' : 'From'}: <span className="font-medium">
                      {selectedMessage.isAdminMessage ? selectedMessage.toUserName : selectedMessage.fromUserName}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Email: {selectedMessage.isAdminMessage ? selectedMessage.toUserEmail : selectedMessage.fromUserEmail}
                  </p>
                  <p className="text-sm text-gray-600">
                    Type: <span className="capitalize">{selectedMessage.type.replace('_', ' ')}</span>
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(selectedMessage.timestamp)}
                </div>
              </div>
            </div>
            
            <div className="prose max-w-none">
              <p className="whitespace-pre-wrap text-gray-700">
                {selectedMessage.message}
              </p>
            </div>

            {selectedMessage.relatedQuoteId && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  Related to Quote ID: {selectedMessage.relatedQuoteId}
                </p>
              </div>
            )}

            {selectedMessage.relatedInvoiceId && (
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-green-700">
                  Related to Invoice ID: {selectedMessage.relatedInvoiceId}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Modal>
  );
};

export default AdminMessaging;
